<template>
	<div class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-40 p-4">
		<div ref="popupContainer" class="account-popup-bg rounded-xl shadow-2xl max-w-2xl w-full overflow-y-auto max-h-[90vh] p-6 md:p-8 relative">

			<button @click="$emit('close')" class="absolute top-4 right-4 p-2  rounded-full transition">
				<img :src="(currentTheme==='dark'||currentTheme==='navy') ? darkX : lightX" alt="Close" class="w-4 h-4" />
			</button>

			<h2 class="text-3xl font-archivomedium text-[var(--text-color)] my-6 md:my-8 text-center">
			Cardware Business Onboarding
			</h2>

			<!-- Error banner -->
			<div v-if="errorMessage" ref="errorBanner" class="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center space-x-2">
				<svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
					<path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.683-1.36 3.448 0l6.518 11.588A1.75 1.75 0 0116.518 17H3.482a1.75 1.75 0 01-1.706-2.313L8.257 3.1zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-4a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0v-2.5A.75.75 0 0110 9z" clip-rule="evenodd"/>
				</svg>
				<span>
					{{ errorMessage }}
				</span>
			</div>

			<form novalidate @submit.prevent="handleSubmit" class="space-y-6">
				<!-- Company Info -->
				<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
							Company Name
						</label>
						<input v-model="form.companyName" type="text" placeholder="ACME Corp." class="block w-full px-4 py-2 rounded-lg bg-[var(--new-dropdown-bg-2-color)] border border-[var(--border-color)] text-[var(--text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)] transition"/>
					</div>
					<div>
						<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
							Registration Number
						</label>
						<input v-model="form.registrationNumber" type="text" placeholder="12345678" class="block w-full px-4 py-2 rounded-lg bg-[var(--new-dropdown-bg-2-color)] border border-[var(--border-color)] text-[var(--text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)] transition"/>
					</div>
					<div class="md:col-span-2">
						<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
							Company Address
						</label>
						<input v-model="form.companyAddress" type="text" placeholder="123 Main St, City, Country" class="block w-full px-4 py-2 rounded-lg bg-[var(--new-dropdown-bg-2-color)] border border-[var(--border-color)] text-[var(--text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)] transition"
						/>
					</div>
				</div>

				<!-- Directors -->
				<fieldset class="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--account-bg)] space-y-4">
					<legend class="px-2 text-[var(--text-color)] font-archivomedium">
						Director(s)
					</legend>

					<div v-for="(d, idx) in form.directors" :key="'dir-'+idx" class="flex items-center space-x-4">
						<div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
							<!-- Name -->
							<div>
								<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
									Name
								</label>
								<input v-model="d.name" type="text" placeholder="Jane Doe" class="block w-full px-4 py-2 rounded-lg bg-[var(--new-dropdown-bg-2-color)] border border-[var(--border-color)] text-[var(--text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)] transition"/>
							</div>

							<!-- File -->
							<div>
								<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
									ID Document
								</label>
								<input @change="e => handleDirectorFile(e, idx)" type="file" accept=".pdf,.jpg,.png" class="block w-full text-sm file:border file:border-[var(--border-color)] file:bg-[var(--new-dropdown-bg-2-color)] file:rounded-lg file:px-3 file:py-2 file:text-[var(--text-color)]"/>
							</div>
						</div>

						<!-- only show remove on idx>0 -->
						<button v-if="idx > 0" type="button" @click="removeDirector(idx)" class="mb-12 md:mb-0 md:mt-4 text-red-500 hover:text-red-700 text-2xl leading-none" title="Remove this director">
							<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
							</svg>
						</button>
					</div>

					<button type="button" @click="addDirector" class="text-sm text-[var(--accent-color)] hover:underline">
						<span class="text-xl">+</span> 
						Add another director
					</button>
				</fieldset>


				<!-- UBOs -->
				<fieldset class="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--account-bg)] space-y-4">
					<legend class="px-2 text-[var(--text-color)] font-archivomedium">
						UBO(s)
					</legend>

					<div v-for="(u, idx) in form.ubos" :key="'ubo-'+idx" class="flex items-start space-x-4" >
						<div class="flex-1 space-y-2">
							<!-- Name & File -->
							<div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
								<div>
									<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
										Name
									</label>
									<input v-model="u.name" type="text" placeholder="John Smith" class="block w-full px-4 py-2 rounded-lg bg-[var(--new-dropdown-bg-2-color)] border border-[var(--border-color)] text-[var(--text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)] transition"/>
								</div>
								<div>
									<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
										ID Document
									</label>
									<input @change="e => handleUboFile(e, idx)" type="file" accept=".pdf,.jpg,.png" class="block w-full text-sm file:border file:border-[var(--border-color)] file:bg-[var(--new-dropdown-bg-2-color)] file:rounded-lg file:px-3 file:py-2 file:text-[var(--text-color)]"/>
								</div>
							</div>
							<!-- % Owned -->
							<div>
								<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
									% Owned
								</label>
								<input v-model.number="u.percentage" type="number" min="0" max="100" step="1" placeholder="25" class="block w-full md:w-1/3 px-4 py-2 rounded-lg bg-[var(--new-dropdown-bg-2-color)] border border-[var(--border-color)] text-[var(--text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)] transition"/>
							</div>
						</div>

						<!-- only show remove on idx>0 -->
						<button v-if="idx > 0" type="button" @click="removeUbo(idx)" class="mt-9 text-red-500 hover:text-red-700 text-2xl leading-none self-start" title="Remove this UBO">
							<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
							</svg>
						</button>
					</div>

					<button type="button" @click="addUbo" class="text-sm text-[var(--accent-color)] hover:underline">
						<span class="text-xl">+</span>
						Add another UBO
					</button>
				</fieldset>


				<!-- Long text fields -->
				<div class="space-y-4">
					<div v-for="field in textFields" :key="field.key">
						<label class="block mb-1 text-sm font-archivoregular text-[var(--text-color)]">
							{{ field.label }}
						</label>
						<textarea v-model="form[field.key]" rows="3" placeholder="..." class="block w-full px-4 py-2 rounded-lg bg-[var(--new-dropdown-bg-2-color)] border border-[var(--border-color)] text-[var(--secondary-text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)] transition resize-none">
						</textarea>
					</div>
				</div>

				<!-- Actions -->
				<div class="flex justify-between space-x-4 pt-4">
					<button type="button" @click="$emit('close')" class="bg-transparent hover:bg-[var(--accent-color)] py-2 px-6 rounded-full border border-[var(--accent-color)] text-[var(--text-color)] hover:text-[var(--hover-text-color)] transition duration-200">
						Cancel
					</button>
					<button type="submit" :disabled="loading" class="px-8 py-2 rounded-full border transition duration-200 bg-[var(--accent-color)] border-[var(--accent-color)] text-[var(--hover-text-color)] hover:bg-transparent hover:text-[var(--text-color)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
						<span v-if="!loading">Submit</span>
						<div v-else class="loader w-5 h-5"></div>
					</button>
				</div>
			</form>
		</div>
	</div>
</template>

<script setup>
	import { ref, nextTick } from 'vue';
	import lightX from '../assets/X.png';
	import darkX from '../assets/XWhite.png';

	const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
	const errorMessage = ref('');
	const errorBanner = ref(null);
	const popupContainer = ref(null);

	const props = defineProps({
		currentTheme: { type: String, required: true },
		loading: { type: Boolean, default: false }
	});
	
	const emit = defineEmits(['close','submit-kyc']);

	// switch to ref instead of reactive
	const form = ref({
		companyName: '',
		companyAddress: '',
		registrationNumber: '',
		directors: [{ name: '', idFile: null }],
		ubos: [{ name: '', idFile: null, percentage: '' }],
		sourceOfFunds: '',
		intendedPurpose: '',
		natureOfBusiness: ''
	});

	const textFields = [
		{ key: 'sourceOfFunds', label: 'Source of Funds' },
		{ key: 'intendedPurpose', label: 'Intended Purpose of Account' },
		{ key: 'natureOfBusiness', label: 'Nature of Business' }
	];

	function addDirector() {
		form.value.directors.push({ name: '', idFile: null });
	}

	function removeDirector(idx) {
		form.value.directors.splice(idx, 1);
	}

	function handleDirectorFile(e, idx) {
		const file = e.target.files[0];
		if (file && file.size > MAX_FILE_SIZE) {
			showError(`Director document "${file.name}" is too large (max 5 MB).`);
			e.target.value = '';
			form.value.directors[idx].idFile = null;
			return;
		}
		form.value.directors[idx].idFile = file;
	}

	function addUbo() {
		form.value.ubos.push({ name: '', idFile: null, percentage: '' });
	}

	function removeUbo(idx) {
		form.value.ubos.splice(idx, 1);
	}

	function handleUboFile(e, idx) {
		const file = e.target.files[0];
			if (file && file.size > MAX_FILE_SIZE) {
			showError(`UBO document "${file.name}" is too large (max 5 MB).`);
			e.target.value = '';
			form.value.ubos[idx].idFile = null;
			return;
		}
		form.value.ubos[idx].idFile = file;
	}


	/*function handleSubmit() {
		// For now, just log the data; backend wiring comes next
		console.log('KYB form submitted:', JSON.parse(JSON.stringify(form)));
		emit('submit-kyc', form);
	}*/

	function handleSubmit() {
		const f = form.value;

		if (f.ubos.some(u => u.percentage > 100)) {
			showError('Each UBO’s ownership % cannot exceed 100%.');
			return;
		}
		
		if (
			!f.companyName.trim() ||
			!f.registrationNumber.trim() ||
			!f.companyAddress.trim() ||
			f.directors.some(d => !d.name.trim() || !d.idFile) ||
			f.ubos.some(u => !u.name.trim() || !u.idFile || u.percentage === '') ||
			!f.sourceOfFunds.trim() ||
			!f.intendedPurpose.trim() ||
			!f.natureOfBusiness.trim()
		) {
			showError('Please complete all required fields.');
			return;
		}
		emit('submit-kyc', f);
	}


	function showError(msg) {
		errorMessage.value = msg;
		nextTick(() => {
			if (errorBanner.value) {
				errorBanner.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		});
		setTimeout(() => { errorMessage.value = '';
		}, 3000);
	}
</script>

<style scoped>

	.loader {
		border: 4px solid #f3f3f3;
		border-top: 4px solid #FC9984;  /* match your brand color */
		border-radius: 50%;
		width: 1rem;
		height: 1rem;
		animation: spin 1s linear infinite;
	}
	@keyframes spin {
		to { transform: rotate(360deg); }
	}

	.account-popup-bg {
		background: var(--account-popup-bg-color);
	}

	input, textarea { font-size: 1rem; }

	.custom-scrollbar {
		overflow-y: auto;
		scrollbar-width: thin;
		scrollbar-color: #888 #f1f1f1;
	}

	.custom-scrollbar::-webkit-scrollbar {
		width: 8px;                        
	}

	.custom-scrollbar::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 16px;
		margin-top: 5%;
		margin-bottom: 5%;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb {
		background-color: #888;            
		border-radius: 8px;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb:hover {
		background-color: #555;           
	}

	input[type="file"]::file-selector-button {
		background: var(--accent-color);
		color: var(--hover-text-color);
		border: none;
		padding: 0.5rem 1rem;
		margin-right: 0.5rem;
		border-radius: 0.375rem;
		cursor: pointer;
	}

	input[type="file"]::-webkit-file-upload-text {
		color: var(--secondary-text-color);
	}

	input[type="file"] {
		color: var(--secondary-text-color);
	}
</style>
